'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { deleteSystem } from '@/services/system-service';
import {
  getFlowCreationSystemId,
  getFlowState,
  updateFlowState,
  clearFlowState,
  hasUnsavedChanges,
  getDefaultFlowState,
  setFlowState,
  isTemporarySystemId,
} from '@/lib/connector-flow-state';
import { domId } from '@/lib/dom-id';
import {
  CONNECTION_NAME_MAX_LENGTH,
  isValidConnectionName,
} from '@/lib/connection-name-validation';
import UnsavedChangesModal from '@/components/unsaved-changes-modal';
import { Button, Input, Textarea } from '@/components/ui';

type AddConnectorProfileStepProps = {
  open: boolean;
  onRequestClose: () => void;
  onContinue: (payload: { systemId: string; name: string; description?: string }) => void;
};

export default function AddConnectorProfileStep({
  open,
  onRequestClose,
  onContinue,
}: AddConnectorProfileStepProps) {
  const t = useTranslations('ConnectorNew');
  const purpose = 'add-connector-modal';
  const [submitting, setSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const formSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t('errors.connectorNameRequired'))
          .max(CONNECTION_NAME_MAX_LENGTH, t('errors.connectorNameTooLong', { max: CONNECTION_NAME_MAX_LENGTH }))
          .refine(isValidConnectionName, t('errors.connectorNameInvalid')),
        description: z.preprocess(
          value => (typeof value === 'string' && value.trim() === '' ? undefined : value),
          z.string().trim().optional()
        ),
      }),
    [t]
  );

  type ConnectorFormValues = z.infer<typeof formSchema>;

  const hydratedDraft = useMemo(() => {
    if (!open) {
      return {
        name: '',
        description: '',
        systemId: '',
      };
    }

    const flowState = getFlowState();

    return {
      name: flowState.connectorData?.name?.trim() || '',
      description: flowState.connectorData?.description || '',
      systemId: flowState.systemId || '',
    };
  }, [open]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<ConnectorFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: hydratedDraft.name,
      description: hydratedDraft.description,
    },
  });

  const name = watch('name') ?? '';
  const description = watch('description') ?? '';
  const canContinue = isValidConnectionName(name) && !submitting;

  const persistConnectorDraft = useCallback((nextName: string, nextDescription: string) => {
    const trimmedName = nextName.trim();
    const trimmedDescription =
      typeof nextDescription === 'string' && nextDescription.trim() !== ''
        ? nextDescription.trim()
        : undefined;
    const flowState = getFlowState();

    if (trimmedName) {
      const effectiveSystemId = flowState.systemId || `temp_${Date.now()}`;

      updateFlowState({
        connectorData: {
          name: trimmedName,
          description: trimmedDescription,
        },
        systemId: effectiveSystemId,
        systemName: trimmedName,
        isTemporary: isTemporarySystemId(effectiveSystemId),
      });
      return;
    }

    const hasOnlyLocalDraft =
      !flowState.previewActions.length &&
      !flowState.hasProcessedApi &&
      !flowState.hasAuthDraft &&
      (!flowState.systemId || isTemporarySystemId(flowState.systemId));

    if (hasOnlyLocalDraft) {
      const resetState = getDefaultFlowState();
      setFlowState({
        ...resetState,
        createdAt: flowState.createdAt,
      });
      return;
    }

    // Keep the persisted draft in sync after a processed API or auth step. A
    // blank name must invalidate forward navigation instead of leaving the
    // previous name available to the stepper.
    updateFlowState({
      connectorData: null,
      systemName: '',
    });
  }, []);

  const nameField = register('name');
  const descriptionField = register('description');

  useEffect(() => {
    if (!open) return;

    reset({
      name: hydratedDraft.name,
      description: hydratedDraft.description,
    });

    if (hydratedDraft.name) {
      const effectiveSystemId = hydratedDraft.systemId || `temp_${Date.now()}`;

      updateFlowState({
        connectorData: {
          name: hydratedDraft.name,
          description:
            typeof hydratedDraft.description === 'string' && hydratedDraft.description.trim() !== ''
              ? hydratedDraft.description
              : undefined,
        },
        systemId: effectiveSystemId,
        systemName: hydratedDraft.name,
        isTemporary: isTemporarySystemId(effectiveSystemId),
      });
    }
  }, [hydratedDraft, open, reset]);

  const handleCancel = () => {
    if (isDirty || hasUnsavedChanges()) {
      setShowCancelModal(true);
      return;
    }

    clearFlowState();
    onRequestClose();
  };

  const handleDiscardChanges = async () => {
    setShowCancelModal(false);

    const flowState = getFlowState();
    // Delete only the connector persisted by this creation flow, as the modal's
    // own discard does: a resumed draft points at a connector it did not create.
    const systemToDelete = getFlowCreationSystemId(flowState);
    // Clear the browser draft and close first. Cleanup of a persisted
    // unfinished system is best-effort and must not keep stale profile data
    // alive while the DELETE request is pending.
    clearFlowState();
    onRequestClose();

    if (systemToDelete) {
      try {
        await deleteSystem(systemToDelete);
      } catch (err) {
        console.error('Failed to delete system:', err);
      }
    }
  };

  const onSubmitForm = handleSubmit(values => {
    setSubmitting(true);

    const connectorData = {
      name: values.name.trim(),
      description: values.description,
    };

    const flowState = getFlowState();
    const effectiveSystemId = flowState.systemId || `temp_${Date.now()}`;
    const isTemporary = isTemporarySystemId(effectiveSystemId);

    updateFlowState({
      connectorData,
      systemId: effectiveSystemId,
      systemName: connectorData.name,
      isTemporary,
    });

    onContinue({
      systemId: effectiveSystemId,
      name: connectorData.name,
      description: connectorData.description,
    });
    setSubmitting(false);
  });

  return (
    <>
      <div className="mx-auto mt-8 flex w-full max-w-[850px] flex-col gap-8">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold leading-7 text-[#0B0809]">{t('title')}</h2>
          <p className="text-sm font-normal leading-5 text-[#444746]">{t('subtitle')}</p>
        </div>

        <form onSubmit={onSubmitForm} className="flex flex-col items-end gap-6">
          <div className="flex w-full flex-col gap-6">
            <div className="flex w-full flex-col items-end gap-3">
              <div className="flex w-full flex-col items-start gap-2">
                <label
                  className="w-full text-base font-medium leading-6 text-[#0B0809]"
                  htmlFor={domId('txt', `${purpose}-name`)}
                >
                  {t('fields.name.label')} <span className="text-red-500">*</span>
                </label>
                <Input
                  id={domId('txt', `${purpose}-name`)}
                  data-testid={domId('txt', `${purpose}-name`)}
                  type="text"
                  autoFocus
                  placeholder={t('fields.name.placeholder')}
                  maxLength={CONNECTION_NAME_MAX_LENGTH}
                  className="h-10 w-full rounded-lg border border-[#E4E4E7] px-6 py-2.5 text-sm font-normal leading-5 text-slate-900 shadow-none placeholder:text-[#9CA3AF] focus:border-slate-400 focus:outline-none focus:ring-0"
                  aria-invalid={Boolean(errors.name)}
                  {...nameField}
                  onChange={event => {
                    nameField.onChange(event);
                    persistConnectorDraft(event.target.value, description);
                  }}
                />
              </div>
            </div>

            <div className="flex w-full flex-col items-end gap-3">
              <div className="flex w-full flex-col items-start gap-2">
                <label
                  className="w-full text-base font-medium leading-6 text-[#0B0809]"
                  htmlFor={domId('are', `${purpose}-description`)}
                >
                  <span>{t('fields.description.label')} </span>
                  <span className="text-[#444746]">{t('fields.description.optional')}</span>
                </label>
                <Textarea
                  id={domId('are', `${purpose}-description`)}
                  data-testid={domId('are', `${purpose}-description`)}
                  placeholder={t('fields.description.placeholder')}
                  rows={4}
                  className="min-h-[104px] w-full resize-none rounded-lg border border-[#E4E4E7] px-6 py-2.5 text-sm font-normal leading-5 text-slate-900 shadow-none placeholder:text-[#9CA3AF] focus:border-slate-400 focus:outline-none focus:ring-0"
                  {...descriptionField}
                  onChange={event => {
                    descriptionField.onChange(event);
                    persistConnectorDraft(name, event.target.value);
                  }}
                />
              </div>
            </div>
          </div>

          {errors.name && (
            <div className="w-full rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {errors.name.message}
            </div>
          )}

          <div className="inline-flex items-start gap-4">
            <Button
              data-testid={domId('btn', `${purpose}-cancel`)}
              type="button"
              onClick={handleCancel}
              variant="unstyled"
              className="h-10 w-28 rounded-lg bg-[#F3F4F6] px-5 text-sm font-medium leading-5 text-slate-900 transition hover:bg-[#E5E7EB]"
              disabled={submitting}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              data-testid={domId('btn', `${purpose}-go-to-add-api`)}
              type="submit"
              variant="unstyled"
              className={`h-10 rounded-lg px-5 text-sm font-medium leading-5 shadow-none transition ${
                canContinue
                  ? 'bg-[linear-gradient(90deg,#713D98_0%,#3E3D98_140.38%)] text-white'
                  : 'bg-[#E5E7EB] text-[#9CA3AF]'
              }`}
              disabled={!canContinue}
            >
              {t('actions.goToAddApi')}
            </Button>
          </div>
        </form>
      </div>

      <UnsavedChangesModal
        isOpen={showCancelModal}
        onKeepEditing={() => setShowCancelModal(false)}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
}
